import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { TOption } from "@/types/common";
import { FILE_INCORRECT } from "@/features/company/constants/excel";
import { TMasterCompanySpecificIndustry } from "@/api/services/global/master/dtos/companySpecificIndustry/get-master-company-specific-industry-list.dto";

const headers = [
  "企業名",
  "企業名かな",
  "業種",
  "設立年",
  "資本金",
  "従業員数",
  "代表者名",
  "代表者役職",
  "電話番号",
  "FAX番号",
  "メールアドレス",
  "ウェブサイト",
  "郵便番号",
  "町名丁目番地号等",
  "分野1",
  "区分1",
  "分野2",
  "区分2",
  "分野3",
  "区分3",
  "担当者名",
  "担当者役職",
  "担当者電話番号",
  "担当者メールアドレス",
  "事業内容・企業特性",
  "メモ",
  "企業管理番号",
  "契約書URL",
];

export const downloadSample = async (listIndustry: TOption[], listCompanySpecificIndustry: TMasterCompanySpecificIndustry[] = []) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("企業情報");
  const industrySheet = workbook.addWorksheet("Industry");
  const companySpecificIndustrySheet = workbook.addWorksheet("CompanySpecificIndustry");

  listIndustry.forEach((item, idx) => {
    industrySheet.getCell("A" + (idx + 1)).value = item.label;
  });
  industrySheet.state = "veryHidden";

  // Add company specific industries to hidden sheet for validation
  listCompanySpecificIndustry.forEach((item, idx) => {
    companySpecificIndustrySheet.getCell("A" + (idx + 1)).value = item.companySpecificIndustry;
  });
  companySpecificIndustrySheet.state = "veryHidden";

  const data = [
    "いろはな建設",
    "いろはなけんせつ",
    "食料品製造業",
    1990,
    5000,
    100,
    "代表",
    "社長",
    "090-1234-5678",
    "090-1234-5678",
    "email@irohana.com",
    "https://irohana.com",
    "100-0000",
    "143番地4",
    "工業製品製造業",
    "機械金属加工｜電気電子機器組立て",
    "",
    "",
    "",
    "",
    "担当",
    "セール",
    "090-1234-5678",
    "email@irohana.com",
    "自動車エンジン部品の製造・販売、品質管理システムの開発、海外展開サポート",
    "来月担当者が変更になる",
    "C0001_介_123",
    "https://drive.google.com/document/testtest",
  ];
  const headerRow = sheet.addRow(headers);
  sheet.addRow(data);

  headerRow.eachCell((cell, colNumber) => {
    cell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "4472C4" },
    };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
    const column = sheet.getColumn(colNumber);
    switch (colNumber) {
      case 1:
        column.width = 25;
        break; // 企業名
      case 2:
        column.width = 25;
        break; // 企業名かな
      case 3:
        column.width = 20;
        break; // 業種
      case 4:
        column.width = 12;
        break; // 設立年
      case 5:
        column.width = 12;
        break; // 資本金
      case 6:
        column.width = 12;
        break; // 従業員数
      case 7:
        column.width = 18;
        break; // 代表者名
      case 8:
        column.width = 18;
        break; // 代表者役職
      case 9:
        column.width = 18;
        break; // 電話番号
      case 10:
        column.width = 18;
        break; // FAX番号
      case 11:
        column.width = 25;
        break; // メールアドレス
      case 12:
        column.width = 25;
        break; // ウェブサイト
      case 13:
        column.width = 15;
        break; // 郵便番号
      case 14:
        column.width = 30;
        break; // 町名丁目番地号等
      case 15:
        column.width = 20;
        break; // 分野1
      case 16:
        column.width = 35;
        break; // 区分1
      case 17:
        column.width = 20;
        break; // 分野2
      case 18:
        column.width = 35;
        break; // 区分2
      case 19:
        column.width = 20;
        break; // 分野3
      case 20:
        column.width = 35;
        break; // 区分3
      case 21:
        column.width = 18;
        break; // 担当者名
      case 22:
        column.width = 18;
        break; // 担当者役職
      case 23:
        column.width = 18;
        break; // 担当者電話番号
      case 24:
        column.width = 25;
        break; // 担当者メールアドレス
      case 25:
        column.width = 40;
        break; // 事業内容・企業特性
      case 26:
        column.width = 30;
        break; // メモ
      case 27:
        column.width = 20;
        break; // 企業管理番号
      case 28:
        column.width = 40;
        break; // 契約書URL
      default:
        column.width = 15;
    }
  });

  const totalRows = 100;
  const companySpecificIndustryCount = listCompanySpecificIndustry.length || 20;

  for (let i = 2; i <= totalRows + 1; i++) {
    // Main industry validation (column C)
    const industryCell = sheet.getCell(`C${i}`);
    industryCell.dataValidation = {
      type: "list",
      formulae: ["=Industry!$A$1:$A$47"],
      showInputMessage: true,
    };

    // Company specific industry validation (columns O, Q, S for 分野1, 分野2, 分野3)
    const industryColumns = ["O", "Q", "S"];
    industryColumns.forEach((col) => {
      const cell = sheet.getCell(`${col}${i}`);
      cell.dataValidation = {
        type: "list",
        formulae: [`=CompanySpecificIndustry!$A$1:$A$${companySpecificIndustryCount}`],
        showInputMessage: true,
      };
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  saveAs(blob, "company-sample.xlsx");
};

export const validateFileFormat = (rowHeader: string[]) => {
  const header = rowHeader.filter((h) => h !== "").map((h) => String(h).trim());

  if (header.length !== headers.length) {
    throw new Error(FILE_INCORRECT);
  }

  for (let i = 0; i < headers.length; i++) {
    if (header[i] !== headers[i]) {
      throw new Error(FILE_INCORRECT);
    }
  }

  return true;
};
